import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Printer, Download } from 'lucide-react';
import api from '../services/api';

const ProgressReportPage = () => {
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('child');
  const [child, setChild] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [completedWork, setCompletedWork] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [childId]);

  const loadData = async () => {
    try {
      const [childrenData, subjectsData, workData, userData] = await Promise.all([
        api.getChildren(),
        api.getSubjects(),
        api.getCompletedWork(childId),
        api.getCurrentUser()
      ]);
      
      const childData = childrenData.find(c => c.id === childId);
      setChild({ ...childData, schoolName: userData.school_name || 'Homeschool', semesters: userData.semesters || [] });
      setSubjects(subjectsData);
      setCompletedWork(workData);
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  if (loading) {
    return <div className="p-8 text-center">Loading report...</div>;
  }

  if (!child) {
    return <div className="p-8 text-center">Student not found</div>;
  }

  // Group work by subject
  const workBySubject = completedWork.reduce((acc, work) => {
    const subject = subjects.find(s => 
      String(s.id) === String(work.subject_id) || 
      (s.replaces_default_id && String(s.replaces_default_id) === String(work.subject_id))
    );
    
    if (subject) {
      const key = subject.id;
      if (!acc[key]) {
        acc[key] = { subject, work: [] };
      }
      acc[key].work.push(work);
    }
    return acc;
  }, {});

  // Calculate subject stats using weighted points
  const subjectStats = Object.values(workBySubject).map(({ subject, work }) => {
    const totalPoints = work.reduce((sum, w) => sum + (w.points_possible || 0), 0);
    const earnedPoints = work.reduce((sum, w) => sum + (w.points_awarded || 0), 0);
    const avgPercentage = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
    const letterGrade = getLetterGrade(avgPercentage);
    return {
      subject,
      avgPercentage: Math.round(avgPercentage),
      letterGrade,
      assignmentCount: work.length
    };
  });

  function getLetterGrade(percentage) {
    if (percentage >= 98) return 'A+';
    if (percentage >= 94) return 'A';
    if (percentage >= 93) return 'A-';
    if (percentage >= 90) return 'B+';
    if (percentage >= 87) return 'B';
    if (percentage >= 85) return 'B-';
    if (percentage >= 82) return 'C+';
    if (percentage >= 79) return 'C';
    if (percentage >= 77) return 'C-';
    if (percentage >= 74) return 'D+';
    if (percentage >= 71) return 'D';
    if (percentage >= 70) return 'D-';
    return 'F';
  }

  // Get current semester name from user's semesters
  const currentSemester = (child.semesters && child.semesters.length > 0) 
    ? child.semesters[0].name 
    : 'Current Semester';

  // Calculate attendance
  // Total days = all unique dates when ANY assignment was due
  const allDueDates = [...new Set(completedWork.map(a => {
    const date = new Date(a.due_date);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  }))];
  
  // Count days as "present" if student submitted ANY assignment that was due on that day
  // (regardless of when they submitted it)
  const daysPresent = allDueDates.filter(dueDate => {
    // Check if any assignment due on this date was submitted
    return completedWork.some(assignment => {
      const assignmentDueDate = new Date(assignment.due_date).toISOString().split('T')[0];
      const hasSubmission = assignment.submitted_date || assignment.completed_date;
      return assignmentDueDate === dueDate && hasSubmission;
    });
  }).length;
  
  const totalDays = allDueDates.length;
  const attendanceRate = totalDays > 0 ? Math.round((daysPresent / totalDays) * 100) : 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print/Download Buttons - Hidden when printing */}
      <div className="no-print sticky top-0 z-10 bg-white border-b p-4 flex justify-end gap-3">
        <Button onClick={handlePrint} variant="outline">
          <Printer className="w-4 h-4 mr-2" />
          Print Report
        </Button>
        <Button onClick={handleDownloadPDF} className="bg-blue-600">
          <Download className="w-4 h-4 mr-2" />
          Save as PDF
        </Button>
      </div>

      {/* Report Content */}
      <div className="max-w-4xl mx-auto bg-white print:mx-0 print:max-w-none print:pt-0" style={{ padding: '1in', paddingTop: '1in' }}>
        {/* Header */}
        <div className="text-center mb-8 border-b-4 border-blue-600 pb-6 print:border-b-2 print:border-slate-300">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Progress Report</h1>
          <p className="text-lg text-slate-600">{child.schoolName}</p>
        </div>

        {/* Student Information */}
        <div className="mb-8 p-6 bg-blue-50 rounded-lg print:bg-transparent print:border print:border-slate-300">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Student Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-600">Student Name</p>
              <p className="text-lg font-semibold text-slate-900">{child.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Grade</p>
              <p className="text-lg font-semibold text-slate-900">{child.grade || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Semester</p>
              <p className="text-lg font-semibold text-slate-900">{currentSemester}</p>
            </div>
          </div>
        </div>

        {/* Subject Grades */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Subject Performance</h2>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-300 p-3 text-left">Subject</th>
                <th className="border border-slate-300 p-3 text-center">Average</th>
                <th className="border border-slate-300 p-3 text-center">Grade</th>
              </tr>
            </thead>
            <tbody>
              {subjectStats.map((stat, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="border border-slate-300 p-3">
                    <span className="font-medium">{stat.subject.name}</span>
                  </td>
                  <td className="border border-slate-300 p-3 text-center">{stat.avgPercentage}%</td>
                  <td className="border border-slate-300 p-3 text-center">
                    <span className="font-bold text-lg">{stat.letterGrade}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Attendance Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Attendance</h2>
          <div className="border border-slate-300 rounded-lg p-6 bg-slate-50 print:bg-transparent">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-600">{daysPresent}</p>
                <p className="text-sm text-slate-600">Days Present</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{totalDays}</p>
                <p className="text-sm text-slate-600">Total Days</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{attendanceRate}%</p>
                <p className="text-sm text-slate-600">Attendance Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-300">
          <p className="text-center text-sm text-slate-600">
            This report reflects the student's academic progress as of {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body::before,
          body::after {
            display: none !important;
          }
          header, nav, .header, [role="banner"] {
            display: none !important;
          }
          @page {
            margin: 1.25in 1in 1in 1in;
            size: letter;
          }
          .print\\:mx-0 {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          .print\\:pt-0 {
            padding-top: 0 !important;
          }
          .print\\:bg-transparent {
            background: transparent !important;
          }
          .print\\:border {
            border: 1px solid #cbd5e1 !important;
          }
          .print\\:border-b-2 {
            border-bottom-width: 2px !important;
          }
          .print\\:border-slate-300 {
            border-color: #cbd5e1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProgressReportPage;
